import { useTranslation } from '@jq4race/i18n';
import type { EventResponse } from '@jq4race/shared';
import {
  Badge,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { Link } from '@tanstack/react-router';
import { CalendarDays, Pencil } from 'lucide-react';

import { EVENT_STATUS_TONE } from '../constants';

interface EventsTableProps {
  events: EventResponse[];
  /** locationId → location name, built by the page from listLocations(). */
  locationNames: Record<string, string>;
  loading: boolean;
  transitioningId: string | null;
  onConfirm: (event: EventResponse) => void;
  onActivate: (event: EventResponse) => void;
}

export function EventsTable({
  events,
  locationNames,
  loading,
  transitioningId,
  onConfirm,
  onActivate,
}: EventsTableProps) {
  const { t } = useTranslation('events');
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('eventsTable.name')}</TableHead>
          <TableHead>{t('eventsTable.location')}</TableHead>
          <TableHead>{t('eventsTable.date')}</TableHead>
          <TableHead>{t('eventsTable.status')}</TableHead>
          <TableHead>{t('eventsTable.capacity')}</TableHead>
          <TableHead className="w-[180px] text-right">{t('eventsTable.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && events.length === 0 ? (
          <SkeletonRows />
        ) : events.length === 0 ? (
          <TableEmpty colSpan={6}>
            <EmptyState />
          </TableEmpty>
        ) : (
          events.map((event) => (
            <TableRow key={event.id}>
              <TableCell>
                <span className="font-medium text-jq-charcoal">{event.name}</span>
              </TableCell>
              <TableCell className="text-jq-graphite">
                {locationNames[event.locationId] ?? event.locationId}
              </TableCell>
              <TableCell className="text-jq-graphite">
                {event.scheduledDate}
                {event.scheduledTime ? ` · ${event.scheduledTime}` : ''}
              </TableCell>
              <TableCell>
                <Badge variant={EVENT_STATUS_TONE[event.status]} className="capitalize">
                  {t(`constants.status.${event.status}`)}
                </Badge>
              </TableCell>
              <TableCell className="text-jq-graphite">{event.expectedCapacity ?? '—'}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {event.status === 'draft' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={transitioningId === event.id}
                      onClick={() => onConfirm(event)}
                    >
                      {transitioningId === event.id
                        ? t('eventsTable.confirming')
                        : t('eventsTable.confirm')}
                    </Button>
                  )}
                  {event.status === 'scheduled' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={transitioningId === event.id}
                      onClick={() => onActivate(event)}
                    >
                      {transitioningId === event.id
                        ? t('eventsTable.activating')
                        : t('eventsTable.activate')}
                    </Button>
                  )}
                  <Link
                    to="/dashboard/events/$eventId/edit"
                    params={{ eventId: event.id }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
                    aria-label={t('eventsTable.edit', { name: event.name })}
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </Link>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 6 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('events');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <CalendarDays className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('eventsTable.empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('eventsTable.empty.description')}</p>
    </div>
  );
}
